import {
  Box,
  CircularProgress,
  Pagination,
  Stack,
  Typography
} from '@mui/material';
import type { ReactNode } from 'react';
import type { PageResponse } from '@/api/types';

interface PaginatedListProps<T> {
  page: PageResponse<T> | undefined;
  isLoading: boolean;
  error: unknown;
  /** Render one item. Mobile-friendly: row-per-card, not table. */
  renderItem: (item: T) => ReactNode;
  /** Called when the user picks a different page (1-indexed in UI; converted to 0-indexed when wired up). */
  onPageChange: (zeroIndexedPage: number) => void;
  emptyMessage?: string;
}

/**
 * Generic paginated list. Mobile-first: renders items as a vertical stack of
 * whatever the caller's {@code renderItem} returns (typically a Card). Use
 * {@link Pagination} from MUI which is touch-friendly.
 */
export function PaginatedList<T>({
  page,
  isLoading,
  error,
  renderItem,
  onPageChange,
  emptyMessage = 'No items.'
}: PaginatedListProps<T>) {
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error) {
    return (
      <Typography color="error">
        Failed to load: {error instanceof Error ? error.message : 'Unknown error'}
      </Typography>
    );
  }
  if (!page || page.items.length === 0) {
    return <Typography color="text.secondary">{emptyMessage}</Typography>;
  }

  return (
    <Stack spacing={2}>
      {page.items.map((item, idx) => (
        <Box key={idx}>{renderItem(item)}</Box>
      ))}
      {page.totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}>
          <Pagination
            count={page.totalPages}
            page={page.page + 1}
            onChange={(_, value) => onPageChange(value - 1)}
            color="primary"
            shape="rounded"
          />
        </Box>
      )}
    </Stack>
  );
}
